import React from 'react';

// Lightweight styled primitives (not a full data-grid abstraction) so every
// table in the app shares the same header/row/cell treatment. Pages still
// own their own column layout and row logic - this only standardizes look.
export const Table = ({ children, className = '' }) => (
  <div className={`overflow-x-auto ${className}`}>
    <table className="w-full text-sm border-collapse">{children}</table>
  </div>
);

// stickyHeader keeps the header row pinned while its container scrolls -
// pass a `max-h-*` + `overflow-y-auto` wrapper around <Table> to use it.
export const THead = ({ children, className = '', sticky = false }) => (
  <thead className={`bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200/60 dark:border-slate-700 ${sticky ? 'sticky top-0 z-10' : ''} ${className}`}>
    {children}
  </thead>
);

// Pass `sortable` + `sortKey` + `currentSort` ({key, direction} from
// useSortableData) + `onSort` to render a clickable, sort-indicating header.
// Plain `<Th>Label</Th>` usage (no sort props) is unaffected.
export const Th = ({ children, className = '', sortable = false, sortKey, currentSort, onSort }) => {
  if (!sortable) {
    return (
      <th className={`px-4 py-3.5 text-left font-bold text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs uppercase tracking-wider whitespace-nowrap ${className}`}>
        {children}
      </th>
    );
  }

  const isActive = currentSort?.key === sortKey;
  const direction = isActive ? currentSort.direction : null;

  return (
    <th className={`px-4 py-3.5 text-left font-bold text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs uppercase tracking-wider whitespace-nowrap ${className}`}>
      <button
        type="button"
        onClick={() => onSort?.(sortKey)}
        className={`inline-flex items-center gap-1.5 group ${isActive ? 'text-primary dark:text-accent' : 'hover:text-slate-700 dark:hover:text-slate-200'} transition-colors`}
        aria-label={`Sort by ${typeof children === 'string' ? children : sortKey}`}
      >
        {children}
        <span className="inline-flex flex-col -space-y-1 text-[8px] leading-none">
          <i className={`fas fa-caret-up ${isActive && direction === 'asc' ? 'opacity-100 text-primary dark:text-accent' : 'opacity-30 group-hover:opacity-60'}`} aria-hidden="true"></i>
          <i className={`fas fa-caret-down ${isActive && direction === 'desc' ? 'opacity-100 text-primary dark:text-accent' : 'opacity-30 group-hover:opacity-60'}`} aria-hidden="true"></i>
        </span>
      </button>
    </th>
  );
};

export const Tr = ({ children, className = '', ...rest }) => (
  <tr
    className={`border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 even:bg-slate-50/40 dark:even:bg-slate-800/20 transition-colors duration-150 ${className}`}
    {...rest}
  >
    {children}
  </tr>
);

export const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 text-slate-600 dark:text-slate-300 text-xs sm:text-sm ${className}`}>{children}</td>
);

export default Table;
